import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { locGiaoVien } from '../utils/invoiceOcrGiaoVien';

/**
 * Ô chọn giáo viên gõ-lọc-chọn, thay cho `<Form.Select>` gốc trình duyệt.
 *
 * Danh sách giáo viên có vài chục người và mã (HOANHST, DUYNAPG…) không dễ
 * nhớ. `<Form.Select>` gốc chỉ nhảy theo CHỮ CÁI ĐẦU khi gõ, không lọc theo
 * chuỗi con và không dán được — phải cuộn tìm bằng mắt, dễ chọn nhầm dòng kế
 * bên (yêu cầu kế toán, 13/10/2026: "tìm như vầy nhiều khi k nhớ mã nám
 * sai"). Ô này là một `<input>` thường nên gõ HOẶC dán (mã hay tên) đều lọc
 * được danh sách bên dưới, không phân biệt hoa/thường, không cần đúng từ đầu.
 *
 * `onChange` LUÔN nhận MÃ (`t.code`), không phải `_id` — cột
 * `MeInvoice.DrivingTeacher` lưu mã ở cả 25.001 hoá đơn đang có, và trang
 * quản lý hoá đơn dựng ô chọn của nó theo `teacher.code`. Màn OCR từng gửi
 * `_id` (ca 17/09/2026): hoá đơn tạo ra mang giá trị không khớp lựa chọn nào
 * ở trang kia, hiện như chưa gán, và chỉ cần chạm vào ô là ghi đè chuỗi rỗng.
 */
export default function TeacherPicker({
  value, onChange, teachers, disabled, placeholder = '— chọn giáo viên —', size = 'sm', title,
}) {
  const current = useMemo(
    () => (teachers || []).find((t) => (t.code || t._id) === value) || null,
    [teachers, value],
  );
  const [open, setOpen] = useState(false);
  const [goTim, setGoTim] = useState('');
  const boxRef = useRef(null);

  const hienThi = open ? goTim : (current ? (current.name || current.code) : (value || ''));

  // Đóng là xoá luôn chữ đã gõ: mở lại mà còn lọc theo lần trước thì danh
  // sách trông như thiếu người.
  const dong = () => { setGoTim(''); setOpen(false); };

  useEffect(() => {
    if (!open) return undefined;
    const onClickNgoai = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) { setGoTim(''); setOpen(false); }
    };
    document.addEventListener('mousedown', onClickNgoai);
    return () => document.removeEventListener('mousedown', onClickNgoai);
  }, [open]);

  // Bỏ dấu khi lọc — gõ "cong" phải ra "Công" (kế toán không nhớ mã).
  const loc = useMemo(() => locGiaoVien(teachers, goTim), [teachers, goTim]);

  const chon = (t) => {
    onChange(t ? (t.code || t._id) : '');
    dong();
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <Form.Control
        size={size}
        placeholder={placeholder}
        disabled={disabled}
        title={title}
        autoComplete='off'
        value={hienThi}
        onFocus={() => setOpen(true)}
        // Tab sang ô khác thì đóng — không thì danh sách của các dòng trước
        // còn mở chồng lên bảng. Chọn bằng chuột không bị ảnh hưởng vì các
        // nút bên dưới chặn mousedown (preventDefault) nên ô không mất focus.
        onBlur={dong}
        onChange={(e) => { setGoTim(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && loc.length === 1) { e.preventDefault(); chon(loc[0]); }
          if (e.key === 'Escape') dong();
        }}
      />
      {open && (
        <div
          className='border rounded bg-white shadow-sm'
          style={{
            position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0,
            maxHeight: 220, overflowY: 'auto',
          }}
        >
          <button
            type='button'
            className='dropdown-item small w-100 text-start border-0 bg-transparent px-2 py-1'
            onMouseDown={(e) => { e.preventDefault(); chon(null); }}
          >
            — bỏ gán —
          </button>
          {loc.length === 0 && <div className='px-2 py-1 small text-muted'>Không khớp giáo viên nào</div>}
          {loc.map((t) => (
            <button
              key={t._id || t.code}
              type='button'
              className='dropdown-item small w-100 text-start border-0 bg-transparent px-2 py-1'
              onMouseDown={(e) => { e.preventDefault(); chon(t); }}
            >
              {t.name || t.code} <span className='text-muted'>({t.code})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
